"use client";

import {
  ArrowRight,
  LockIcon,
  RefreshCwIcon,
  ShieldCheckIcon,
} from "lucide-react";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";

import { MACOS_DOWNLOADS, WINDOWS_DOWNLOAD } from "./download-links";
import DesktopHeroArt from "./hero-art";
import { AppleLogo, WindowsLogo } from "./os-icons";
import { scrollToSetup, useDetectedOS } from "./use-detected-os";

const trustSignals = [
  { icon: ShieldCheckIcon, label: "Signed & notarized" },
  { icon: LockIcon, label: "Records only when you start" },
  { icon: RefreshCwIcon, label: "Auto-updating" },
];

export default function DesktopHero() {
  const os = useDetectedOS();

  const primary =
    os === "windows"
      ? {
          href: WINDOWS_DOWNLOAD.href,
          label: "Download for Windows",
          Icon: WindowsLogo,
        }
      : {
          href: MACOS_DOWNLOADS.appleSilicon.href,
          label: "Download for Mac",
          Icon: AppleLogo,
        };

  return (
    <section className="relative px-5 pt-14 pb-16 md:px-8 md:pt-16 md:pb-24 lg:px-14">
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-8">
          <div className="order-2 flex flex-col items-start gap-6 text-white lg:order-1">
            <div className="rounded-full border border-white/15 bg-white/10 px-3 py-1 backdrop-blur-sm">
              <p className="font-mono text-sm tracking-[-0.28px] text-white/90">
                Desktop app
              </p>
            </div>

            <h1 className="text-heading-40 sm:text-heading-48 md:text-heading-56 max-w-[560px] text-balance">
              Record your work. Train the next AI.
            </h1>
            <p className="max-w-[520px] text-base text-white/80 md:text-lg">
              The Terac desktop app captures how experts get real tasks done,
              the demonstrations that teach computer-use AI agents. Install it,
              complete your assigned tasks, and get paid for verified work.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                href={primary.href}
                onClick={scrollToSetup}
                className="text-marketing-blue group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-7 text-sm font-medium transition-colors hover:bg-white/90"
              >
                <primary.Icon className="h-4 w-4" />
                {primary.label}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#downloads"
                className="group inline-flex items-center gap-1.5 text-sm text-white/70 transition-colors hover:text-white"
              >
                See all downloads and setup steps
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {trustSignals.map((signal) => (
                <div
                  key={signal.label}
                  className="flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-xs text-white/90 backdrop-blur-sm sm:text-sm"
                >
                  <signal.icon className="size-3.5 text-white/80" />
                  {signal.label}
                </div>
              ))}
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <DesktopHeroArt />
          </div>
        </div>
      </Container>
    </section>
  );
}
